import { useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import type { AccessState } from '@/ds'
import { isLanguage } from '@/i18n'
import { accesosService } from '@/services/accesos'
import type { Stay } from '@/services/reserva'

const OPENED_RESET_MS = 2000
const RETURN_TO_ACTIVE_MS = 700

/**
 * Estado de apertura por acceso (DESIGN.md §4 llave: activa → abriendo → abierta | error; abierta vuelve
 * se despide con un rebobinado y vuelve a activa tras 2 s). Lo comparten el inicio (B1) y la pantalla de Accesos (B4).
 */
export function useKeyOpener(stay: Stay) {
  const { i18n } = useTranslation()
  const [states, setStates] = useState<Record<string, AccessState>>({})
  const timers = useRef<Record<string, ReturnType<typeof setTimeout>>>({})

  useEffect(() => {
    const current = timers.current
    return () => {
      for (const id of Object.values(current)) clearTimeout(id)
    }
  }, [])

  const open = useCallback(
    async (accessId: string) => {
      setStates((s) => ({ ...s, [accessId]: 'opening' }))
      const lang = isLanguage(i18n.language) ? i18n.language : 'es'
      // E17 recibe el idioma sin mapear (`en`, no `gb`), como SwipeButton.tsx de la app actual.
      const result = await accesosService.open(stay, accessId, lang)
      setStates((s) => ({ ...s, [accessId]: result.status === 'opened' ? 'opened' : 'error' }))
      if (result.status === 'opened') {
        clearTimeout(timers.current[accessId])
        timers.current[accessId] = setTimeout(() => {
          setStates((s) => ({ ...s, [accessId]: 'returning' }))
          timers.current[accessId] = setTimeout(() => setStates((s) => ({ ...s, [accessId]: 'active' })), RETURN_TO_ACTIVE_MS)
        }, OPENED_RESET_MS)
      }
    },
    [stay, i18n.language],
  )

  return { states, open }
}
